import React, { useEffect, useMemo, useRef, useState } from 'react'
import { styles } from '../../../../../../styles/tailwind-config'
import { PHOTO, useInterventionInput } from '../../../../../../context/InterventionInputContext'
import { Trans } from '@lingui/react/macro'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../../../../../components/ui/sheet'
import { CameraIcon, CheckIcon, PencilIcon, Trash2Icon } from 'lucide-react'
import { Label } from '../../../../../../components/ui/label'
import { Input } from '../../../../../../components/ui/input'
import { Button } from '../../../../../../components/ui/button'
import { useBoundStore } from '../../../../../../zustand'
import { Combobox } from '../../../../../../components/ui/combobox'
import { Separator } from '../../../../../../components/ui/separator'
import { format } from 'date-fns/format'
import { useAuth } from '../../../../../../context/AuthProvider'
import { safeFormat } from '../../../../../../lib/date'

type MachineTypeItem = {
  value: string;
  label: string;
  type: string;
  description: string;
}

const EditTechnicalUnit = ({ machineId }: { machineId: number }) => {
  const [open, setOpen] = useState(false)
  const { intervention, setintervention, photos, setphotos } = useInterventionInput()
  const [tab, setTab] = useState<'omschrijving' | 'details'>('omschrijving')

  const machine = useMemo(() => (
    intervention?.m_aMACHINES?.find(m => m.m_iMACHINEID === machineId) ?? null
  ), [intervention, machineId])

  // Form state
  const [merk, setMerk] = useState('')
  const [merkId, setMerkId] = useState(0)
  const [typeId, setTypeId] = useState(0)
  const [modelId, setModelId] = useState(0)
  const [type, setType] = useState('')
  const [model, setModel] = useState('')
  const [soort, setSoort] = useState('')
  const [serienr, setSerienr] = useState('')
  const [bouwjaar, setBouwjaar] = useState('')
  const [datumInstall, setDatumInstall] = useState<Date | null>(null)
  const [omschrijving, setOmschrijving] = useState('')
  const [locatie, setLocatie] = useState('')
  const [datumMaintance, setDatumMaintance] = useState<Date | null>(null)
  const [datumEndWaranty, setDatumEndWaranty] = useState<Date | null>(null)
  const [localPhotos, setLocalPhotos] = useState<typeof photos>([])
  const hasInitialized = useRef(false)

  const fileId = `techunit-file-${machineId}`;

  // Sync form fields when modal opens
  useEffect(() => {
    if (open && machine && !hasInitialized.current) {
      setTab('omschrijving')
      hasInitialized.current = true

      setMerk(machine.m_sMERK || '')
      setMerkId(machine.m_iMERKID || 0)
      setTypeId(machine.m_iTYPEID || 0)
      setModelId(machine.m_iMODELID || 0)
      setType(machine.m_sTYPE || '')
      setModel(machine.m_sMODEL || '')
      setSoort(machine.m_sSOORTMACH || '')
      setSerienr(machine.m_sSERIENR || '')
      setBouwjaar(machine.m_sBOUWJAAR || '')
      setDatumInstall(machine.m_dDTINSTALL ? new Date(machine.m_dDTINSTALL) : null)
      setOmschrijving(machine.m_sOMS || '')
      setLocatie(machine.m_sLOCATIONINFO || '')
      setDatumMaintance(machine.m_sBEGINONDERHOUD ? new Date(machine.m_sBEGINONDERHOUD) : null)
      setDatumEndWaranty(machine.m_dGARANTIEDATE ? new Date(machine.m_dGARANTIEDATE) : null)
      const filtered = (photos ?? []).filter((p) =>
        p.soort === 'FOTOMACHINE' &&
        p.machineid === machineId &&
        p.interventionid === String(intervention?.m_iINTERVENTIESID ?? '')
      )
      setLocalPhotos(filtered)
      setselected_images([])
      setselected_images([])
    }
    if (!open) {
      hasInitialized.current = false // reset on close
    }
  }, [open, machine, photos, machineId, intervention])

  const machinebrands = useBoundStore((s) => s.machinebrands)
  const machinetypes = useBoundStore((s) => s.machinetypes)
  const parameters = useBoundStore((s) => s.parameters)
  const machinemodels = useBoundStore((s) => s.machinemodels)

  const brandOptions = useMemo(() => machinebrands
    .sort((a, b) => a.m_sMERK.localeCompare(b.m_sMERK))
    .map(brand => ({ label: brand.m_sMERK, value: brand.m_sMERK, id: brand.m_iMERKID })), [machinebrands])

  const filteredTypes = useMemo(() => (
    machinetypes
      ?.filter(t => t.m_iMERKID === merkId)
      .map(t => ({
        label: t.m_sDESCRIPTION || t.m_sTYPE,
        value: t.m_sTYPE,
        type: t.m_sTYPE,
        description: t.m_sDESCRIPTION,
        id: t.m_iTYPEID
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  ), [machinetypes, merkId])

  const machineTypeOptions = useMemo(() => (
    parameters
      .filter(p => p.m_iTYPE.toString() === '10')
      .map(p => ({ label: p.m_sOMS, value: p.m_sOMS }))
      .sort((a, b) => a.label.localeCompare(b.label))
  ), [parameters])

  const filteredModels = useMemo(() => (
    machinemodels
      ?.filter(t => t.m_iTYPEID === typeId)
      .map(t => ({
        label: t.m_sDESCRIPTION,
        value: t.m_sDESCRIPTION,
        id: t.m_iMACHINEMODELID
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  ), [machinemodels, typeId])

  const handleSave = () => {
    if (!intervention || !machine) return

    const updatedMachine: Machine = {
      ...machine,
      m_sMERK: merk,
      m_iMERKID: merkId,
      m_iTYPEID: typeId,
      m_iMODELID: modelId,
      m_sTYPE: type,
      m_sMODEL: model,
      m_sSOORTMACH: soort,
      m_sSERIENR: serienr,
      m_sBOUWJAAR: bouwjaar,
      m_dDTINSTALL: datumInstall ?? null,
      m_sOMS: omschrijving,
      m_sLOCATIONINFO: locatie,
      m_sBEGINONDERHOUD: datumMaintance ?? null,
      m_dGARANTIEDATE: datumEndWaranty ?? null,
    }

    const updatedMachines = intervention.m_aMACHINES?.map(m =>
      m.m_iMACHINEID === machineId ? updatedMachine : m
    )

    setintervention({ ...intervention, m_aMACHINES: updatedMachines })

    setphotos(prev => ([
      ...((prev ?? []).filter(p =>
        !(p.soort === 'FOTOMACHINE' &&
          p.machineid === machineId &&
          p.interventionid === String(intervention?.m_iINTERVENTIESID ?? ''))
      )),
      // add the edited local set
      ...localPhotos
    ]))

    setOpen(false)
  }


  const [selected_images, setselected_images] = useState<number[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleToggleImage(index: number) {
    setselected_images(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    )
  }

  function handleRemoveImages() {
    setLocalPhotos(prev => prev.filter((_, i) => !selected_images.includes(i)))
    setselected_images([])
  }

  function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? [])
    if (files.length === 0) return

    const newPhotos: PHOTO[] = []
    files.forEach(file => {
      const reader = new FileReader()
      reader.onload = e => {
        const base64Image = e.target?.result as string
        newPhotos.push({
          attestid: '',
          customerdocid: '',
          docid: '',
          interventionid: String(intervention?.m_iINTERVENTIESID ?? ''),
          photo: base64Image,
          machineid: machineId,
          otherusedpartid: '',
          soort: 'FOTOMACHINE',
          timestamp: format(new Date(), 'yyyyMMddHHmmss'),
          usedpartid: ''
        })
        // when all files done, append atomically
        if (newPhotos.length === files.length) {
          setLocalPhotos(prev => ([...(prev ?? []), ...newPhotos]))
          if (fileInputRef.current) fileInputRef.current.value = ''
        }
      }
      reader.readAsDataURL(file)
    })
  }

  return (
    <Sheet
      open={open}
      onOpenChange={setOpen}
    >
      <button
        onClick={() => setOpen(true)}
        className='self-end flex items-center justify-center w-8 h-8 text-yellow-600 rounded-full aspect-square'
      >
        <PencilIcon className='w-4 h-4' />
      </button>

      <SheetContent className='w-full min-w-[90vw] flex flex-col overflow-auto max-h-dvh'>
        <SheetHeader>
          <SheetTitle><Trans>Technische eenheid bewerken</Trans></SheetTitle>
        </SheetHeader>

        {/* Tabs */}
        <div className='flex gap-2 mt-4'>
          {['omschrijving', 'details'].map((key) => (
            <button
              key={key}
              className={`px-4 py-2 rounded-t ${tab === key ? 'bg-muted text-primary font-medium' : 'bg-muted/30 text-muted-foreground'}`}
              onClick={() => setTab(key as typeof tab)}
            >
              <Trans>{key === 'omschrijving' ? 'Omschrijving' : 'Details'}</Trans>
            </button>
          ))}
        </div>

        {/* Form */}
        <div className='p-4 border rounded-t-none rounded-md border-t-0 bg-background shadow-inner'>
          {tab === 'omschrijving' && (
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <Label><Trans>Merk</Trans></Label>
                <Combobox
                  options={brandOptions}
                  selectedValue={merk}
                  onValueChange={({ value }) => {
                    const selected = machinebrands.find(b => b.m_sMERK === value)
                    const valueStr = String(value)

                    if (!selected) {
                      setMerk(valueStr)
                      setMerkId(0)
                      setTypeId(0)
                      setType('')
                      setModel('')
                      setModelId(0)
                      return
                    }

                    setMerk(valueStr)
                    setMerkId(selected.m_iMERKID ?? 0)
                    setTypeId(0)
                    setType('')
                    setModel('')
                    setModelId(0)
                  }}
                  placeholder='Selecteer een merk...'
                />
              </div>
              <div>
                <Label><Trans>Type</Trans></Label>
                <Combobox<MachineTypeItem>
                  options={filteredTypes}
                  selectedValue={type}
                  onValueChange={({ value }) => {
                    const selected = filteredTypes.find(b => b.value === value)
                    const valueStr = String(value)

                    if (!selected) {
                      setTypeId(0)
                      setType(valueStr)
                      setModel('')
                      setModelId(0)
                      return
                    }

                    setTypeId(selected.id)
                    setType(valueStr)
                    setModel('')
                    setModelId(0)
                  }}
                  placeholder="Selecteer een type..."
                  disabled={merkId === 0}
                  renderHeader={() => (
                    <div className="sticky top-0 z-10 bg-background grid grid-cols-2 px-2 py-1 text-xs font-medium text-muted-foreground border-b">
                      <span>Type</span>
                      <span>Omschrijving</span>
                    </div>
                  )}
                  renderOption={(item) => (
                    <div className="grid grid-cols-2 gap-4 w-full text-left">
                      <span className="text-sm text-foreground">{item.type}</span>
                      <span className="text-sm text-muted-foreground">{item.description || '-'}</span>
                    </div>
                  )}
                />


              </div>
              <div className='md:col-span-2'>
                <Label><Trans>Model</Trans></Label>
                <Combobox
                  options={filteredModels}
                  selectedValue={model}
                  onValueChange={({ value }) => {
                    const selected = filteredModels.find(b => b.value === value)
                    const valueStr = String(value)

                    if (!selected) {
                      setModel(valueStr)
                      setModelId(0)
                      return
                    }

                    setModel(valueStr)
                    setModelId(selected.id ?? 0)
                  }}
                  placeholder='Selecteer een model...'
                />
              </div>
              <div className='md:col-span-2'>
                <Label><Trans>Soort technische eenheid</Trans></Label>
                <Combobox
                  options={machineTypeOptions}
                  selectedValue={soort}
                  onValueChange={({ value }) => setSoort(String(value))}
                  placeholder='Selecteer een soort...'
                />
              </div>
              <div className='md:col-span-2'>
                <Label><Trans>Serienummer</Trans></Label>
                <Input value={serienr} onChange={(e) => setSerienr(e.target.value)} />
              </div>
              <div>
                <Label><Trans>Datum installatie</Trans></Label>
                <Input
                  type='date'
                  value={
                    datumInstall instanceof Date && !isNaN(datumInstall.getTime())
                      ? safeFormat(datumInstall)
                      : ''
                  }
                  onChange={(e) => setDatumInstall(new Date(e.target.value))}
                />
              </div>
              <div>
                <Label><Trans>Bouwjaar</Trans></Label>
                <Input value={bouwjaar} onChange={(e) => setBouwjaar(e.target.value)} />
              </div>
              <div className='md:col-span-2'>
                <Label><Trans>Omschrijving</Trans></Label>
                <textarea className='w-full border rounded p-2 min-h-[160px]' value={omschrijving} onChange={(e) => setOmschrijving(e.target.value)} />
              </div>
              <div className='md:col-span-2'>
                <Label><Trans>Locatie info</Trans></Label>
                <textarea className='w-full border rounded p-2 min-h-[160px]' value={locatie} onChange={(e) => setLocatie(e.target.value)} />
              </div>
            </div>
          )}

          {tab === 'details' && (
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <Label><Trans>Externe id</Trans></Label>
                <Input value={machine?.m_sEXTNUMMER} disabled />
              </div>
              <div>
                <Label><Trans>Interne id</Trans></Label>
                <Input value={machine?.m_sINTNUMMER} disabled />
              </div>
              <div>
                <Label><Trans>Begin onderhoud</Trans></Label>
                <Input
                  type='date'
                  value={
                    datumMaintance instanceof Date && !isNaN(datumMaintance.getTime())
                      ? safeFormat(datumMaintance)
                      : ''
                  }
                  onChange={(e) => setDatumMaintance(new Date(e.target.value))}
                />
              </div>
              <div>
                <Label><Trans>Datum eind garantie</Trans></Label>
                <Input
                  type='date'
                  value={
                    datumEndWaranty instanceof Date && !isNaN(datumEndWaranty.getTime())
                      ? safeFormat(datumEndWaranty)
                      : ''
                  }
                  onChange={(e) => setDatumEndWaranty(new Date(e.target.value))}
                />
              </div>
              <Separator className='md:col-span-2' />
              <div className='md:col-span-2 flex flex-col gap-2'>
                <h4 className={`${styles.h4}`}><Trans>Foto's toevoegen</Trans></h4>
                <div className='flex items-center justify-between h-10 p-1 border rounded-full'>
                  <input
                    id={fileId}
                    ref={fileInputRef}
                    type='file'
                    accept='image/*'
                    className='absolute w-px h-px -m-px p-0 overflow-hidden border-0 clip-rect'
                    onChange={handleFileChange}
                    multiple
                    onClick={(e) => { (e.currentTarget as HTMLInputElement).value = '' }}
                  />
                  <label
                    htmlFor={fileId}
                    className='flex items-center justify-center w-8 h-full text-blue-600 rounded-full aspect-square cursor-pointer'
                    aria-label="Foto's kiezen of maken"
                  >
                    <CameraIcon className='w-4 h-4' />
                  </label>

                  <button
                    onClick={handleRemoveImages}
                    disabled={selected_images.length === 0}
                    className='flex items-center justify-center w-8 h-full text-red-600 rounded-full aspect-square disabled:opacity-30'
                  >
                    <Trash2Icon className='w-4 h-4' />
                  </button>
                </div>
                <div className='grid grid-cols-2 gap-4 mt-2 lg:grid-cols-4'>
                  {localPhotos.map((photo, i) => (
                    <button
                      key={`photo-${i}`}
                      onClick={() => handleToggleImage(i)}
                      className='relative w-full overflow-hidden rounded-md min-h-32 aspect-video bg-neutral-100'
                    >
                      {selected_images.includes(i) && (
                        <div className='absolute right-2 top-2 flex items-center justify-center w-6 h-6 text-white bg-blue-600 border-2 border-white rounded-full'>
                          <CheckIcon className='w-4 h-4' />
                        </div>
                      )}
                      <img src={photo.photo || ''} className='object-cover w-full h-full' alt='MountQ image' />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className='flex justify-end gap-2 mt-4'>
          <Button variant='ghost' onClick={() => setOpen(false)}><Trans>Annuleren</Trans></Button>
          <Button onClick={handleSave}><Trans>Bevestigen</Trans></Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}


const TechnicalUnit = () => {
  const { intervention } = useInterventionInput()
  const { auth } = useAuth()

  const allowedDbIds = new Set([1, 7]) // 1 = lecot live, 7 = lecot test
  const dbid = auth?.data?.databases[0]?.dbid
  const showEditTechnicalUnit = typeof dbid === 'number' && allowedDbIds.has(dbid)

  return (
    <div>
      <h3 className={`${styles.h3} mb-4`}><Trans>Technische eenheid informatie</Trans></h3>
      <div className='flex flex-col gap-4'>
        {intervention?.m_aMACHINES?.map((machine, i) => (
          <div className='flex flex-col gap-4 p-2 border rounded-md' key={i}>
            <div className='flex justify-between items-start'>
              <div>
                <p className='font-medium text-neutral-500'><Trans>Merk</Trans></p>
                <p className='font-medium text-neutral-800'>{machine?.m_sMERK || '-'}</p>
              </div>
              {/* <EditTechnicalUnit machineId={machine.m_iMACHINEID} /> */}
              {showEditTechnicalUnit && <EditTechnicalUnit machineId={machine.m_iMACHINEID} />}
            </div>
            <div>
              <p className='font-medium text-neutral-500'><Trans>Type</Trans></p>
              <p className='font-medium text-neutral-800'>{machine?.m_sTYPEDESCRIPTION || machine?.m_sTYPE || '-'}</p>
            </div>
            <div>
              <p className='font-medium text-neutral-500'><Trans>Omschrijving</Trans></p>
              <p className='font-medium text-neutral-800'>{machine?.m_sOMS || '-'}</p>
            </div>
            <div>
              <p className='font-medium text-neutral-500'><Trans>Bouwjaar</Trans></p>
              <p className='font-medium text-neutral-800'>{machine?.m_sBOUWJAAR || '-'}</p>
            </div>
            <div>
              <p className='font-medium text-neutral-500'><Trans>Locatie info</Trans></p>
              <p className='font-medium text-neutral-800'>{machine?.m_sLOCATIONINFO || '-'}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default TechnicalUnit